import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "../contexts/TranslationContext";
import { useForm } from "react-hook-form";
import { passwordValidation, passwordConfirmValidation } from "../utils/validation.js";
import { Loader2 } from "lucide-react";

export default function ResetPasswordPage() {
  const [isLoading, setIsLoading] = useState(false);
  const { t, locale } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const { register, handleSubmit, watch, trigger, formState: { errors, isSubmitted } } = useForm();
  const password = watch("newPassword");

  // Revalida ao trocar de idioma para regenerar as mensagens de erro no idioma atual
  useEffect(() => {
    if (isSubmitted) trigger();
  }, [locale, isSubmitted, trigger]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword: data.newPassword }),
      });

      const responseData = await res.json();

      if (res.ok) {
        toast.success(t('resetPassword.success'));
        setTimeout(() => navigate("/login"), 2000);
      } else {
        toast.error(responseData.error || t('resetPassword.error'));
      }
    } catch (err) {
      toast.error(t('resetPassword.networkError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex-1 flex items-center justify-center p-4">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-full max-w-md bg-[#111] border border-white/10 rounded-xl shadow-lg p-8 space-y-4"
        noValidate
      >
        <h2 className="text-xl font-bold text-center text-white">
          {t('resetPassword.title')}
        </h2>

        <div>
          <input
            type="password"
            placeholder={t('resetPassword.placeholder')}
            {...register("newPassword", passwordValidation(t))}
            className={`w-full p-3 border rounded-md bg-[#0a0a0a] text-white placeholder-gray-500 focus:outline-none focus:border-brand-500 ${
              errors.newPassword ? 'border-red-500' : 'border-white/10'
            }`}
          />
          {errors.newPassword && (
            <p className="text-red-500 text-sm mt-1 text-left">{errors.newPassword.message}</p>
          )}
        </div>

        <div>
          <input
            type="password"
            placeholder={t('validation.passwordConfirmRequired')}
            {...register("confirmPassword", passwordConfirmValidation(t, password))}
            className={`w-full p-3 border rounded-md bg-[#0a0a0a] text-white placeholder-gray-500 focus:outline-none focus:border-brand-500 ${
              errors.confirmPassword ? 'border-red-500' : 'border-white/10'
            }`}
          />
          {errors.confirmPassword && (
            <p className="text-red-500 text-sm mt-1 text-left">{errors.confirmPassword.message}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-brand-500 hover:bg-brand-400 text-black p-3 rounded-md transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading && <Loader2 className="animate-spin" size={20} />}
          {t('resetPassword.button')}
        </button>
      </form>
    </div>
  );
}
